import React from "react";
import { Link } from "react-router-dom";
import LegalPage, {
  ContactLink,
  LegalSection,
  OPERATOR,
} from "../components/LegalPage";

function Privacy() {
  return (
    <LegalPage title="Privacy policy">
      <p>
        Plateo helps you find jobs, prepare applications and keep track of them.
        It's run by {OPERATOR} ("we" or "us"). This page explains what we
        collect, why, who else sees it, how long we keep it, and the choices you
        have. It covers the Plateo website, the Plateo Chrome extension and the
        Platypus, and is part of our <Link to="/terms">terms of service</Link>.
        By creating an account you agree to it - the signup form asks you to
        confirm that, and we record the version you agreed to and when.
      </p>
      <p>
        <strong>The short version:</strong> we only use your information to run
        Plateo for you. We don't sell personal information, we don't share it
        for advertising, we don't show ads, and we don't use tracking or
        analytics tools. We never provide people's email addresses. If anything
        below ever conflicts with this short version, the detailed sections
        govern.
      </p>

      <LegalSection title="What we collect">
        <ul>
          <li>
            <strong>Your account:</strong> name, email address, and your
            password stored only as a bcrypt hash (nobody can read it, including
            us). We also record whether your email is confirmed, when you last
            signed in, whether an admin has suspended the account, and the
            version and date of the terms and this policy you accepted.
          </li>
          <li>
            <strong>Access requests and AI credits:</strong> if you ask for AI
            access, the short reason you give, an admin's decision and optional
            note, the date, and how many AI credits you were granted and have
            used, plus daily counts of AI requests (so we can apply limits).
            Admins who run this site can see account details (name, email,
            access status, credits and usage) to manage accounts - they are not
            shown your profile's demographic answers.
          </li>
          <li>
            <strong>Your profile and Platypus memory:</strong> contact details,
            links (LinkedIn, GitHub, portfolio), address, phone, work
            authorization, citizenship, sponsorship needs, salary expectation,
            start date, clearance, relocation/on-site and similar standard
            answers, the experiences, projects, education, publications, awards,
            patents, certifications, skills and languages you add to Platypus's
            memory (or that Plateo fills from your resume with a free text parse
            - no AI - or that Platypus proposes from what you tell it and you
            approve), and the settings you choose. These can include{" "}
            <em>optional, sensitive</em> demographic answers some applications
            ask for (gender, race or ethnicity, Hispanic origin, veteran and
            disability status). You can always leave them unset or as “Ask me”.
          </li>
          <li>
            <strong>Your documents:</strong> resumes and transcripts you upload
            (the files themselves, their names, sizes and types), the text we
            extract from resumes so features can use it, and the cover-letter
            and outreach email templates you save, including which resume a
            template should attach.
          </li>
          <li>
            <strong>Your job search:</strong> jobs you save or Platypus prepares
            (title, company, description copied from the posting, stage, dates,
            notes, activity history, match scores and reasons), tailored resumes
            and cover letters, application questions and answers, answers Plateo
            remembers so you don't type them twice, and contacts you save (name,
            headline/title, LinkedIn link, kind, whether you emailed them, and
            an email address only if you found one yourself and added it).
          </li>
          <li>
            <strong>Platypus settings:</strong> the roles, locations, filters,
            extra job boards and daily schedule (including your browser's time
            zone) you set, a log of each run, and which postings it has already
            seen so it doesn't prepare them twice.
          </li>
          <li>
            <strong>People-finder searches:</strong> when a job's Contacts tab
            looks for people, we send a search made only from that job's
            company, role and (non-remote) city - never your name, email, resume
            or profile - to our search provider (below). The public results
            (names, headlines, LinkedIn links and short snippets) are cached in
            a cache <em>shared by all users for up to 30 days</em>, because the
            same company search gives everyone the same answer.
          </li>
          <li>
            <strong>If you connect Gmail:</strong> your Gmail address and an
            encrypted Google refresh token (AES-256-GCM; the key is never stored
            with the data), and when you connected. When you click “Send email”,
            the message you wrote - recipient, subject, body and any resume the
            template attaches - goes through Gmail as described below. We also
            email that Gmail address a security alert when it's connected.
          </li>
          <li>
            <strong>Technical basics:</strong> a sign-in cookie (
            <code>plateo_token</code>, http-only, 7 days) - the only cookie we
            set. The extension separately keeps a 30-day sign-in token and the
            Plateo address in Chrome's own storage, and, only during an
            application it is filling, remembers per-tab which job it is working
            on. Our hosting, database, storage and email providers may keep
            ordinary server logs (such as IP address, browser and timestamps)
            under their own policies to run and secure their services.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="What the Chrome extension does">
        <p>The extension only acts when you ask it to:</p>
        <ul>
          <li>
            When you click <strong>Save this job</strong>, it reads the text of
            that page and sends it to Plateo to pull out the job details.
          </li>
          <li>
            When you click <strong>Fill in this application</strong>, or open an
            application from Plateo, it reads the form's questions on that tab,
            asks Plateo for answers from your profile, remembered answers and
            (where AI is involved) the AI flow below, fills them in, and
            attaches your resume, cover letter or transcript. For an application
            Plateo opened, it keeps filling later pages of that same application
            and, when the site shows a confirmation page, marks the job as
            applied (you can undo that). Demographic and legal-attestation
            questions are left for you to answer yourself.
          </li>
          <li>
            It runs on the Plateo website only to connect to your account.
          </li>
        </ul>
        <p className="mb-0">
          It doesn't collect your browsing history and doesn't read pages you
          haven't asked it to work on. It never submits an application for you;
          you always review and submit it yourself. Plateo's use of information
          received through the extension follows the{" "}
          <a
            href="https://developer.chrome.com/docs/webstore/program-policies/policies"
            target="_blank"
            rel="noreferrer"
          >
            Chrome Web Store User Data Policy
          </a>
          , including its Limited Use requirements.
        </p>
      </LegalSection>

      <LegalSection title="How we use it">
        <ul className="mb-0">
          <li>
            To run the features you use: searching and tracking jobs, writing
            cover letters, tailoring resumes, drafting outreach, remembering
            answers, running Platypus on your schedule, and filling in
            applications.
          </li>
          <li>
            To send the emails the product needs: confirming your address,
            resetting your password, telling you an access decision, and the
            Gmail security alerts described above. If you email us at our
            contact address, we use that to reply.
          </li>
          <li>
            To review access requests, apply AI credit and rate limits, prevent
            abuse, keep accounts secure, and fix problems.
          </li>
          <li>
            We do <strong>not</strong> use your content to train AI models, and
            we don't use it for advertising.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Who else sees it">
        <p>
          We share information only with the services Plateo needs to work, and
          only what each one needs. They process it for us under their own terms
          and privacy policies, and may process it in the United States or other
          countries:
        </p>
        <ul>
          <li>
            <strong>OpenAI</strong> (AI writing and screening), only when you
            use an AI feature or Platypus does: the job posting, the application
            questions, and the relevant parts of your resume, profile and
            Platypus memory (experiences, projects, education, publications,
            awards, patents, certifications, skills and languages, including
            each entry's location) - name, email, location, links, work
            authorization, citizenship, sponsorship needs, salary expectation
            and start date. Filling memory from a resume is done with code on
            our servers and is <em>not</em> sent to OpenAI. Your{" "}
            <em>demographic answers are never sent to OpenAI</em>; questions
            about demographics or legal attestations are left for you, and
            Plateo fills demographic answers in itself (on the employer's form,
            only when you run the extension and then submit). We don't send
            OpenAI your phone number or postal address for AI writing. Under
            OpenAI's API terms, data sent through its API isn't used to train
            their models.
          </li>
          <li>
            <strong>Serper</strong> (a Google-search API), only for the people
            finder: the company/role/city search described above. It returns
            public LinkedIn profile information that search engines already
            index. Plateo never fetches linkedin.com itself and doesn't scrape
            LinkedIn.
          </li>
          <li>
            <strong>Job boards and a16z Speedrun:</strong> public Greenhouse,
            Lever and Ashby company boards, the free a16z Speedrun Talent Network
            jobs API, Simplify's public GitHub lists, and Remotive receive your
            search terms, location and filters (we identify ourselves as
            “plateo” on the a16z API). When you give Plateo a job link, our
            server fetches that page to read the posting. Company sponsorship
            history shown in results comes from public USCIS H-1B records and
            isn't about you.
          </li>
          <li>
            <strong>Google (Gmail)</strong>, only if you choose “Connect Gmail”
            in your Profile. You grant two permissions:
            <em> send email as you</em> (<code>gmail.send</code>) and{" "}
            <em> see your email address</em> (<code>userinfo.email</code>, so we
            can show which account is connected). Plateo can never read, search,
            list or change your inbox. Sending happens only when you click “Send
            email”, from your address, with the content you reviewed.
            Disconnecting in your Profile deletes our copy of the token and asks
            Google to revoke the grant; deleting your account does the same.
            Google API use follows the{" "}
            <a
              href="https://developers.google.com/terms/api-services-user-data-policy"
              target="_blank"
              rel="noreferrer"
            >
              Google API Services User Data Policy
            </a>
            , including its Limited Use requirements. Without a connected Gmail,
            Plateo cannot send outreach for you (drafting, “Open in email app”
            and “Mark as sent” still work) - outreach is never sent from a
            Plateo address.
          </li>
          <li>
            <strong>Our infrastructure providers:</strong> Render (hosting),
            MongoDB Atlas (database), Cloudflare (DNS, file storage in an
            S3-compatible bucket, and Email Routing, which forwards mail sent to
            our contact address to the site owner's inbox), and Resend via SMTP
            (sending our account emails, above). In local development, files may
            instead sit in a folder on the developer's machine.
          </li>
          <li>
            <strong>LinkedIn and other sites we link to:</strong> Plateo doesn't
            provide email addresses. If you open a person's LinkedIn page or an
            employer's site, that site sees you under its own policy. If you
            find an email yourself (for example with Hunter.io or Apollo.io) and
            add it to a contact, it's stored only on that contact and used only
            when you choose to email that person.
          </li>
          <li>
            <strong>Employer application sites:</strong> when the extension
            fills a form, the answers, files and demographic answers it fills in
            are on that employer's page; they go to the employer only when{" "}
            <em>you</em> submit, under the employer's policy.
          </li>
        </ul>
        <p className="mb-0">
          We'll also disclose information if the law requires it, or if it's
          needed to protect Plateo, our users or others from harm or fraud. If
          Plateo changes hands, information may transfer with it, and we'll tell
          you first where the law requires.
        </p>
      </LegalSection>

      <LegalSection title="Keeping and deleting your data">
        <p>We keep your information while you have an account, except:</p>
        <ul>
          <li>
            People-finder cached results delete themselves after 30 days;
            one-time email links expire after 24 hours (confirm), 1 hour
            (password reset) or 30 days (the Gmail “this wasn't me” link), and
            only a hash of each link is stored.
          </li>
          <li>
            Copies in our providers' routine backups are overwritten in the
            ordinary course of their operations after live data is deleted.
          </li>
        </ul>
        <ul className="mb-0">
          <li>
            <strong>Delete your account</strong> from your Profile page at any
            time (you'll be asked for your password). That permanently removes,
            from our live systems, your account, profile, jobs, documents and
            uploaded files, templates, remembered answers, Platypus settings and
            run history, usage counts, pending one-time links, and your Gmail
            connection - and revokes the Gmail grant at Google. It also ends
            your sessions, including the extension's.
          </li>
          <li>
            <strong>Start fresh</strong> from your Profile page clears your jobs
            and documents but keeps your account and Gmail connection.
          </li>
          <li>
            Remembered answers and the experiences, projects, education,
            publications, awards, patents, certifications, skills and languages
            in Platypus's memory can be removed one at a time from your Memory
            page, and a Gmail connection can be removed at any time with
            “Disconnect”.
          </li>
          <li>
            Uninstalling the extension removes everything it stored in your
            browser; disconnecting it first also ends its token's usefulness
            when your password changes or the account is deleted.
          </li>
          <li>
            This policy can't delete what you already sent to an employer, a
            person you emailed through Gmail, or copies those recipients or your
            email provider keep.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Your choices and rights">
        <ul className="mb-0">
          <li>
            Most of your information is visible and editable by you in the app
            (Profile, Documents, jobs and contacts) - correcting it there
            corrects it everywhere in Plateo.
          </li>
          <li>
            At <ContactLink /> you can ask for a copy of your information, ask
            us to correct or delete something you can't change in the app, or
            object to or restrict how we use it. We'll respond without undue
            delay and, where your local law (for example UK/EU GDPR or
            California's CCPA/CPRA) gives you extra rights, we honour them. We
            don't sell or “share” personal information as those laws define
            those words, and we won't treat you differently for exercising a
            right.
          </li>
          <li>
            Demographic answers are always optional. Leaving them unset means
            Plateo leaves those questions for you.
          </li>
        </ul>
      </LegalSection>

      <LegalSection title="Security">
        <p className="mb-0">
          Connections to Plateo are encrypted (HTTPS) and we set security
          headers and rate limits on the site and API. Passwords are
          bcrypt-hashed, one-time email links are stored only as hashes, Gmail
          tokens are encrypted, files are stored privately (never at a public
          link - downloads require signing in as the owner), and every API
          request checks that the data belongs to the signed-in user. Admin
          pages require an admin account. No system is perfectly secure, so
          please use a strong password you don't use anywhere else and tell us
          at <ContactLink /> if you think your account has been misused.
        </p>
      </LegalSection>

      <LegalSection title="Children">
        <p className="mb-0">
          Plateo is for job seekers and isn't meant for anyone under 16. We
          don't knowingly collect information from anyone younger, and if we
          learn we have, we'll delete the account.
        </p>
      </LegalSection>

      <LegalSection title="Changes and questions">
        <p className="mb-0">
          If we change this policy we'll update the date at the top and the
          version recorded for new accounts, and tell you in the app or by email
          before a change that matters takes effect. Continuing to use Plateo
          after that means the new policy applies; if you don't agree, you can
          delete your account. For questions, or to ask for a copy of your data,
          contact us at <ContactLink />.
        </p>
      </LegalSection>
    </LegalPage>
  );
}

export default Privacy;
